import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';

import { AnimationItem } from 'lottie-web';
import { ModulesService } from '../../services/modules.service';
import { LoginAnimationService } from '../../services/login-animation.service';

@Component({
  selector: 'app-background-overlay',
  standalone: true,
  imports: [LottieComponent],
  templateUrl: './background-overlay.component.html',
  styleUrl: './background-overlay.component.scss',
})
export class BackgroundOverlayComponent implements OnInit, OnDestroy {
  private routeSubscription: Subscription = new Subscription();
  private loginAnimationSubscription: Subscription = new Subscription();

  // Route group tracking
  currentRouteGroup: number = 0;

  // Visibility flags
  showGroup1Lottie = signal(false);
  showModuleImage = signal(false);
  addOpacity = signal(false);
  showBackgroundLottie = signal(true);

  // Anim Options
  bdcAnimOptions: AnimationOptions = {
    path: '/assets/lottie/fond_bedici.json',
    loop: true,
    autoplay: true,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };
  geoAnimOptions: AnimationOptions = {
    path: '/assets/lottie/fond_geometrique.json',
    loop: true,
    autoplay: true,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  // Content sources
  moduleImageSrc: string = '';

  bdcBackgroundAnimItem: AnimationItem | null = null;
  geoBackgroundAnimItem: AnimationItem | null = null;

  constructor(
    private router: Router,
    private moduleService: ModulesService,
    private loginAnimationService: LoginAnimationService,
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.updateContentBasedOnRoute(event.url);
    });

    // Subscribe to login animation service
    this.loginAnimationSubscription = this.loginAnimationService.showGeometricAnimation$.subscribe((show) => {
      if (this.router.url === '/login') {
        this.showGroup1Lottie.set(show);
      }
    });

    // Initialize with current route
    this.updateContentBasedOnRoute(this.router.url);
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    this.loginAnimationSubscription.unsubscribe();
  }

  private updateContentBasedOnRoute(url: string): void {
    this.currentRouteGroup = this.determineRouteGroup(url);
    this.updateVisibilityAndContent(url);
  }

  private determineRouteGroup(url: string): number {
    // Group 1: Authentication and general pages
    if (
      url === '/' ||
      url === '/login' ||
      url === '/signup' ||
      url === '/forgot-password' ||
      url.includes('/verify') ||
      url.includes('/reset-password') ||
      url.includes('bridge') ||
      url === '/parcourstp' ||
      url === '/survey' ||
      url.includes('/onboarding') ||
      url.includes('/payment') ||
      url.includes('/offers')
    ) {
      return 1;
    }
    // Group 2: User dashboard and settings
    else if (
      url === '/settings' ||
      url === '/follow_up' ||
      url === '/follow_up_parent' ||
      url === '/teaching_sheets' ||
      url === '/helptp' ||
      url === '/help' ||
      url === '/homec' ||
      url === '/hometp' ||
      url === '/update_avatar'
    ) {
      return 2;
    }
    // Group 3: Module pages (child)
    else if (url.includes('/module_start/') || url.includes('/module_end/')) {
      return 3;
    }
    // Group 4: Module take pages (child)
    else if (url.includes('/module_take/')) {
      return 4;
    }
    // Group 5: Module pages (teacher/parent)
    else if (url.includes('/module_start_tp') || url.includes('/module_end_tp')) {
      return 5;
    }
    // Group 6: Module take pages (teacher/parent)
    else if (url.includes('/module_take_tp')) {
      return 6;
    }

    return 1;
  }

  private updateVisibilityAndContent(url: string): void {
    // Reset
    this.showGroup1Lottie.set(false);
    this.showModuleImage.set(false);
    this.addOpacity.set(false);
    this.showBackgroundLottie.set(true);

    switch (this.currentRouteGroup) {
      case 1:
        // Group 1: Hide background lottie on login, show geometric animation later
        if (url === '/login') {
          this.showBackgroundLottie.set(false);
          // showGroup1Lottie will be controlled by login component
        } else {
          this.showGroup1Lottie.set(true);
          this.addOpacity.set(true);
        }
        break;

      case 3:
        // Group 3: Module pages (child) - show image
        this.showModuleImage.set(true);
        this.moduleImageSrc = this.getModuleImageSrc(url, 3);
        break;

      case 4:
        // Group 4: Module take pages (child) - show image
        this.showModuleImage.set(true);
        this.moduleImageSrc = this.getModuleImageSrc(url, 4);
        break;

      case 5:
        // Group 5: Module pages (teacher/parent) - show image
        this.showModuleImage.set(true);
        this.moduleImageSrc = this.getModuleImageSrc(url, 5);
        break;

      case 6:
        // Group 6: Module take pages (teacher/parent) - show image
        this.showModuleImage.set(true);
        this.moduleImageSrc = this.getModuleImageSrc(url, 6);
        break;
    }
  }

  private getModuleImageSrc(url: string, group: number): string {
    const array = url.split('/');
    const moduleId = array[array.length - 1];

    switch (group) {
      case 3:
        // Group 3: Module pages (child)
        return this.moduleService.getNewModuleBgImgPath(moduleId, undefined);

      case 4:
        // Group 4: Module take pages (child)
        return this.moduleService.getNewModuleBgImgPath(moduleId, undefined, true);

      case 5:
        // Group 5: Module pages (teacher/parent)
        const colorTp = array[array.length - 2];
        return this.moduleService.getNewModuleBgImgPath(moduleId, colorTp);

      case 6:
        // Group 6: Module take pages (teacher/parent)
        const colorTpTake = array[array.length - 2];
        return this.moduleService.getNewModuleBgImgPath(moduleId, colorTpTake, true);

      default:
        return '';
    }
  }

  bdcAnimationCreated(animationItem: AnimationItem): void {
    this.bdcBackgroundAnimItem = animationItem;
  }

  geoAnimationCreated(animationItem: AnimationItem): void {
    this.bdcBackgroundAnimItem = animationItem;
  }
}
